import React from 'react';
import { formatRating } from '../../utils/format';

interface UserStats {
  total_questions_answered: number;
  correct_answers: number;
  accuracy_percentage: number;
  current_streak: number;
  current_rating: number;
}

interface PersonalStatsSectionProps {
  userStats: UserStats | null;
  isLoading: boolean;
}

const PersonalStatsSection: React.FC<PersonalStatsSectionProps> = ({
  userStats,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Personal Overview</h2>
        </div>
        <div className="p-6">
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mango-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Personal Overview</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Your current performance statistics
        </p>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Current Rating */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <div className="p-2 bg-mango-100 dark:bg-mango-900 rounded-lg">
                <svg className="h-6 w-6 text-mango-600 dark:text-mango-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatRating(userStats?.current_rating ?? 10)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Current Rating</p>
          </div>

          {/* Questions Solved */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {userStats?.total_questions_answered || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Questions Solved</p>
          </div>

          {/* Day Streak */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {userStats?.current_streak || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Day Streak</p>
          </div>

          {/* Accuracy */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {userStats?.accuracy_percentage || 0}%
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Accuracy</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalStatsSection;
